import React, { useState, useEffect, createContext, useContext } from 'react';
import { ChevronDown, User, Users, Check } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Button } from './ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

// Create context for active account
const ActiveAccountContext = createContext(null);

export const useActiveAccount = () => {
  const context = useContext(ActiveAccountContext);
  if (!context) {
    throw new Error('useActiveAccount must be used within ActiveAccountProvider');
  }
  return context;
};

export const ActiveAccountProvider = ({ children }) => {
  const [activeAccountId, setActiveAccountId] = useState(() => {
    return localStorage.getItem('activeAccountId') || sessionStorage.getItem('activeAccountId') || null;
  });
  const [accounts, setAccounts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasFetched, setHasFetched] = useState(false);

  const fetchAccounts = async () => {
    const token = localStorage.getItem('token');
    console.log('fetchAccounts called, token exists:', !!token);
    
    if (!token) {
      setIsLoading(false);
      return;
    }

    try {
      console.log('Fetching accounts from:', `${API}/api/auth/accounts`);
      const response = await fetch(`${API}/api/auth/accounts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('Accounts response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('Accounts data:', data);
        setAccounts(data);
        setHasFetched(true);
        
        // If no active account set, or active account not in list, default to personal
        if (!activeAccountId || !data.find(a => a.account_id === activeAccountId)) {
          const personalAccount = data.find(a => a.type === 'personal');
          if (personalAccount) {
            setActiveAccountId(personalAccount.account_id);
            localStorage.setItem('activeAccountId', personalAccount.account_id);
          }
        }
      } else {
        console.log('Accounts fetch failed:', response.status);
      }
    } catch (error) {
      console.error('Failed to fetch accounts:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Listen for storage changes (token being set after login)
  useEffect(() => {
    // Initial check
    fetchAccounts();
    
    // Check again after a short delay in case login is happening
    const tokenCheckInterval = setInterval(() => {
      const token = localStorage.getItem('token');
      if (token && !hasFetched) {
        fetchAccounts();
      }
    }, 500);
    
    // Also listen for storage events
    const handleStorageChange = (e) => {
      if (e.key === 'token' && e.newValue) {
        fetchAccounts();
      }
    };
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      clearInterval(tokenCheckInterval);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [hasFetched]);

  const switchAccount = (accountId) => {
    setActiveAccountId(accountId);
    localStorage.setItem('activeAccountId', accountId);
    sessionStorage.setItem('activeAccountId', accountId);
    // Force hard navigation to ensure clean state on iOS
    window.location.href = '/dashboard?switched=' + accountId + '&t=' + Date.now();
  };

  const getActiveAccount = () => {
    return accounts.find(a => a.account_id === activeAccountId) || accounts[0];
  };

  const value = {
    activeAccountId,
    accounts,
    isLoading,
    switchAccount,
    getActiveAccount,
    refreshAccounts: fetchAccounts,
    hasMultipleAccounts: accounts.length > 1
  };

  return (
    <ActiveAccountContext.Provider value={value}>
      {children}
    </ActiveAccountContext.Provider>
  );
};

export const AccountSwitcher = () => {
  const { accounts, activeAccountId, switchAccount, isLoading, hasMultipleAccounts } = useActiveAccount();

  // Debug logging
  console.log('AccountSwitcher: accounts=', accounts.length, 'hasMultiple=', hasMultipleAccounts, 'loading=', isLoading);

  if (isLoading) {
    return null;
  }
  
  // Don't show if no accounts loaded yet
  if (accounts.length === 0) {
    return null;
  }

  const activeAccount = accounts.find(a => a.account_id === activeAccountId) || accounts[0];
  
  // Get display name for the active account
  const getAccountDisplayName = (account) => {
    if (account?.type === 'personal') {
      return `${account.owner_name || 'My'}'s Account`;
    }
    return account?.name || 'Team Account';
  };

  // If only one account, show simple label (no dropdown)
  if (!hasMultipleAccounts) {
    return (
      <div 
        className="flex items-center gap-2 px-3 py-2 rounded-lg bg-forest/5 border border-forest/10"
        data-testid="account-display"
      >
        <User className="w-4 h-4 text-forest" />
        <span className="text-sm font-medium text-forest truncate max-w-[180px]">
          {getAccountDisplayName(activeAccount)}
        </span>
      </div>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="outline" 
          size="default" 
          className="w-full justify-between gap-2 bg-white border-forest/20 hover:bg-forest/5 h-auto py-2"
          data-testid="account-switcher"
        >
          <div className="flex items-center gap-2 min-w-0">
            {activeAccount?.type === 'personal' ? (
              <User className="w-4 h-4 text-forest flex-shrink-0" />
            ) : (
              <Users className="w-4 h-4 text-saddle flex-shrink-0" />
            )}
            <span className="truncate text-sm font-medium">
              {getAccountDisplayName(activeAccount)}
            </span>
          </div>
          <ChevronDown className="w-4 h-4 opacity-50 flex-shrink-0" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64">
        <DropdownMenuLabel className="text-xs text-muted-foreground uppercase tracking-wide">
          Switch Account
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {accounts.map((account) => (
          <DropdownMenuItem
            key={account.account_id}
            onClick={() => switchAccount(account.account_id)}
            className="cursor-pointer"
            data-testid={`account-option-${account.type}`}
          >
            <div className="flex items-center gap-3 w-full">
              {account.type === 'personal' ? (
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
                  <User className="w-4 h-4 text-emerald-700" />
                </div>
              ) : (
                <div className="w-8 h-8 rounded-full bg-saddle/10 flex items-center justify-center">
                  <Users className="w-4 h-4 text-saddle" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm truncate">{getAccountDisplayName(account)}</p>
                <p className="text-xs text-muted-foreground truncate">
                  {account.type === 'personal' ? 'Personal horses' : `Team • ${account.owner_name}`}
                </p>
              </div>
              {account.account_id === activeAccountId && (
                <Check className="w-4 h-4 text-forest" />
              )}
            </div>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default AccountSwitcher;
